/**
 * How a Resource Plan's two statuses read to a user — the one label map the plan screens,
 * the server's messages and the Changes feed all share.
 *
 * The stored values are unchanged; only the words are. Two of them differ from their
 * value (workspace feat-staffing-plan-status-workflow, 2026-10-28):
 *
 *   • Sales status `Draft` reads **Open** — the plan is owned by sales to submit.
 *   • Resourcing status `Returned` reads **Rejected** — a Resource Manager rejected it.
 *
 * Every other value reads as its own name with the camel case split ("SoftCommitted" →
 * "Soft Committed"). A `null` Resourcing status is "Not set": nobody has submitted the plan.
 */

const PLAN_STATUS_LABEL: Record<string, string> = {
  Draft: "Open",
  SoftCommitted: "Soft Committed",
  HandedOver: "Handed Over",
};

/** `"Draft"` → `"Open"`, `"SoftCommitted"` → `"Soft Committed"`, `"Won"` → `"Won"`. */
export function planStatusLabel(status: string): string {
  return PLAN_STATUS_LABEL[status] ?? status.replace(/([a-z])([A-Z])/g, "$1 $2");
}

const RESOURCING_STATUS_LABEL: Record<string, string> = {
  InReview: "In Review",
  Staffing: "Staffing",
  Staffed: "Staffed",
  Returned: "Rejected",
};

/** `"InReview"` → `"In Review"`, `"Returned"` → `"Rejected"`, `null` → `"Not set"`. */
export function resourcingStatusLabel(status: string | null | undefined): string {
  if (!status) return "Not set";
  return RESOURCING_STATUS_LABEL[status] ?? status.replace(/([a-z])([A-Z])/g, "$1 $2");
}

/**
 * Milestone text the system wrote before these labels changed ("Status Draft → Proposed",
 * "Returned to its author") still says the old words. The Changes feed passes system-written
 * summaries through this when it reads them, so history reads in today's terms without any
 * stored row being rewritten. Never applied to anything a person typed.
 */
export function relabelLegacyStatusText(text: string): string {
  return text
    .replace(/\bReturned to its author\b/g, "Rejected")
    .replace(/\bResourcing (\S.*?) → Returned\b/g, "Resourcing $1 → Rejected")
    .replace(/\bResourcing Returned →/g, "Resourcing Rejected →")
    .replace(/\bStatus Draft →/g, "Status Open →")
    .replace(/\b(Status [^·→]+?) → Draft\b/g, "$1 → Open");
}
